<template>
  <q-editor
    ref="editorRef"
    v-bind="$attrs"
    v-model="model"
    min-height="5rem"
    paragraph-tag="p"
    content-class="tw-prose"
    :toolbar="defaultToolbar"
    :definitions="definitions"
    @paste="onPaste"
  />
</template>

<script setup>
import {
  editorBold,
  editorCode,
  editorImage,
  editorItalic,
  editorLink,
  editorOrdered,
  editorUnderline,
  editorUnlink,
  editorUnordered,
  toDuotoneIcon,
  toSolidIcon,
} from "common/defaultIcons";
import { useQuasar } from "quasar";
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from "vue";

// Quasar
const $q = useQuasar();

const model = defineModel({
  type: String,
  default: "",
  required: true,
});

// Refs
const editorRef = useTemplateRef("editorRef");

const props = defineProps({
  headings: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Boolean,
    default: false,
  },
  link: {
    type: Boolean,
    default: false,
  },
  code: {
    type: Boolean,
    default: false,
  },
});

const defaultToolbar = computed(() => {
  return [
    [
      {
        label: $q.lang.editor.align,
        icon: "fa-duotone fa-align-left",
        fixedLabel: true,
        list: "only-icons",
        options: ["left", "center", "right", "justify"],
      },
      ...(props.headings
        ? [
            {
              label: "Titel",
              options: ["h1", "h2", "h3"],
            },
          ]
        : []),
    ],
    ["bold", "italic", "underline"],
    ["unordered", "ordered"],
    ...(props.code ? [["code"]] : []),
    ...(props.link ? [["link", "unlink"]] : []),
    ...(props.images ? [["images"]] : []),
  ];
});

const uploadImage = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";

  input.onchange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      $q.notify({
        type: "negative",
        message: "Afbeelding is te groot. Maximaal toegestane grootte is 5MB.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      const imageUrl = e.target.result;
      insertImageToEditor(imageUrl);
    };
    reader.readAsDataURL(file);
  };

  input.click();
};

const insertImageToEditor = (src) => {
  const edit = editorRef.value;
  edit.caret.restore();
  edit.runCmd("insertHTML", `<img src="${src}" style="max-width: 100%;" />`);
  edit.focus();
};

const definitions = computed(() => {
  return {
    images: {
      icon: toDuotoneIcon(editorImage),
      tip: "Afbeelding invoegen",
      handler: uploadImage,
    },
    bold: {
      icon: toSolidIcon(editorBold),
    },
    italic: {
      icon: toSolidIcon(editorItalic),
    },
    underline: {
      icon: toSolidIcon(editorUnderline),
    },
    unordered: {
      icon: toDuotoneIcon(editorUnordered),
    },
    ordered: {
      icon: toDuotoneIcon(editorOrdered),
    },
    link: {
      icon: toDuotoneIcon(editorLink),
      tip: "Link toevoegen",
      handler: () => {
        addLinkHandler();
      },
    },
    unlink: {
      icon: toDuotoneIcon(editorUnlink),
      tip: "Link verwijderen",
      handler: () => {
        removeLink();
      },
    },
    code: {
      icon: toDuotoneIcon(editorCode),
    },
  };
});

const removeLink = () => {
  const edit = editorRef.value;
  if (!edit) return;

  edit.focus();
  edit.runCmd("unlink");
};

const addLinkHandler = () => {
  const edit = editorRef.value;

  const selection = window.getSelection();
  let savedRange = null;
  let selectedText = "";
  let selectedHref = "";

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    savedRange = range.cloneRange();
    selectedText = selection
      .toString()
      .replace(/\u200B/g, "")
      .trim();

    let node = selection.anchorNode;
    while (node && node.nodeType === 3) node = node.parentNode;
    const anchor = node?.closest?.("a");
    if (anchor && anchor.href) selectedHref = anchor.href;
  }

  const isValidUrl = (text) => {
    try {
      const url = new URL(text);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const initialUrl = isValidUrl(selectedHref)
    ? selectedHref
    : isValidUrl(selectedText)
      ? selectedText
      : "https://";

  const initialTitle = selectedText || "";

  $q.dialog({
    title: "Voer de URL in",
    message: "Plak of typ de URL voor de link:",
    prompt: {
      model: initialUrl,
      type: "text",
      label: "URL",
      isValid: (val) => isValidUrl(val),
      rules: [
        (val) => !!val || "URL is verplicht",
        (val) => isValidUrl(val) || "URL moet geldig zijn (http/https)",
      ],
    },
    cancel: true,
    persistent: true,
  }).onOk((url) => {
    $q.dialog({
      title: "Linktitel",
      message: "Tekst die zichtbaar moet zijn als link:",
      prompt: {
        model: initialTitle,
        type: "text",
        label: "Titel",
      },
      cancel: true,
      persistent: true,
    }).onOk((title) => {
      setTimeout(() => {
        edit.focus();

        if (savedRange) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange);

          savedRange.deleteContents();
        }

        const safeTitle = title.replace(/"/g, "&quot;");
        edit.runCmd(
          "insertHTML",
          `<a href="${url}" target="_blank" rel="noopener noreferrer">${safeTitle}</a>`,
        );

        edit.focus();
      }, 0);
    });
  });
};

const onPaste = (evt) => {
  // Let inputs do their thing, so we don't break pasting of links.
  if (evt.target.nodeName === "INPUT") return;
  let text, onPasteStripFormattingIEPaste;
  evt.preventDefault();
  evt.stopPropagation();
  if (evt.originalEvent && evt.originalEvent.clipboardData.getData) {
    text = evt.originalEvent.clipboardData.getData("text/plain");
    editorRef.value.runCmd("insertText", text);
  } else if (evt.clipboardData && evt.clipboardData.getData) {
    text = evt.clipboardData.getData("text/plain");
    editorRef.value.runCmd("insertText", text);
  } else if (window.clipboardData && window.clipboardData.getData) {
    if (!onPasteStripFormattingIEPaste) {
      onPasteStripFormattingIEPaste = true;
      editorRef.value.runCmd("ms-pasteTextOnly", text);
    }
    onPasteStripFormattingIEPaste = false;
  }
};

const observeEditorAnchors = (editorElement, model) => {
  const updateModelFromEditor = () => {
    model.value = editorElement.innerHTML;
  };

  const observer = new MutationObserver(() => {
    const anchors = editorElement.querySelectorAll("a");
    let changed = false;

    anchors.forEach((anchor) => {
      if (
        !anchor.hasAttribute("target") ||
        anchor.getAttribute("target") !== "_blank"
      ) {
        anchor.setAttribute("target", "_blank");
        changed = true;
      }
      if (
        !anchor.hasAttribute("rel") ||
        anchor.getAttribute("rel") !== "noopener noreferrer"
      ) {
        anchor.setAttribute("rel", "noopener noreferrer");
        changed = true;
      }
    });

    if (changed) {
      updateModelFromEditor();
    }
  });

  observer.observe(editorElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return () => observer.disconnect();
};

let stopObserving = null;
onMounted(() => {
  if (!editorRef.value) {
    return;
  }

  const editorEl = editorRef.value.getContentEl();

  stopObserving = observeEditorAnchors(editorEl, model);
});

onBeforeUnmount(() => {
  if (stopObserving) {
    stopObserving();
  }
});
</script>

<style lang="scss">
.q-editor {
  &__content {
    @apply tw-prose;

    a {
      @apply tw-cursor-text;
    }
  }
}
</style>
